import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useAlert } from "@/hooks/useAlert";
import { useInvalidateBasket } from "@/services/api";
import { AlertTypes } from "@/utils/atoms";
import {
  isIccidMissingError,
  isInvalidBasketStatusError,
  isMnpDataMissingError,
  isTotalAmountChangedError,
} from "@/utils/basketErrorUtils";

// ICCID/MNP cannot be entered on an existing subscription, so a basket missing
// them is as unusable as a closed one.
const refreshableBasketErrors = [
  { matches: isTotalAmountChangedError, messageKey: "totalAmountChanged" },
  { matches: isInvalidBasketStatusError, messageKey: "basketReloaded" },
  { matches: isIccidMissingError, messageKey: "basketReloaded" },
  { matches: isMnpDataMissingError, messageKey: "basketReloaded" },
] as const;

type Params = {
  basketId: number | undefined;
  refreshBasket: () => void;
};

/**
 * For checkouts on an existing subscription (tariff change, roaming, watch SIM)
 * where Compax basket errors are resolved by ordering a fresh basket.
 * The returned handler shows a warning, invalidates the rejected basket (an open
 * one can block the new order), calls `refreshBasket`, and returns true when it
 * handled the error.
 */
export const useBasketRefreshOnError = ({
  basketId,
  refreshBasket,
}: Params) => {
  const { t } = useTranslation();
  const showAlert = useAlert();
  const { mutateAsync: invalidateBasket } = useInvalidateBasket();

  return useCallback(
    (err: unknown): boolean => {
      const basketError = refreshableBasketErrors.find(({ matches }) =>
        matches(err),
      );

      if (!basketError) {
        return false;
      }

      showAlert({
        type: AlertTypes.warning,
        text: t(
          `Onboarding.sections.checkout.basketErrors.${basketError.messageKey}`,
        ),
      });

      if (basketId === undefined) {
        refreshBasket();

        return true;
      }

      invalidateBasket({ basketId })
        .catch((error) => {
          // Compax may already have closed the basket; ordering a new one still works.
          console.error("Failed to invalidate rejected basket:", error);
        })
        .finally(refreshBasket);

      return true;
    },
    [basketId, invalidateBasket, refreshBasket, showAlert, t],
  );
};
